import type { AxiosResponse } from 'axios';

/**
 * Generic file download utility
 * Extracts filename from response headers and triggers browser download
 * 
 * @param response - Axios response containing blob data
 * @param defaultFilename - Fallback filename if not provided in headers
 * @param mimeType - MIME type of the file (default: 'application/octet-stream')
 */
export const downloadFile = (
  response: AxiosResponse<Blob>,
  defaultFilename: string,
  mimeType = 'application/octet-stream',
): void => {
  // Extract filename from Content-Disposition header if available
  const contentDisposition = response.headers['content-disposition'] as string | undefined;
  let filename = defaultFilename;

  if (contentDisposition) {
    const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/i;
    const filenameMatch = filenameRegex.exec(contentDisposition);
    if (filenameMatch?.[1]) {
      // Remove quotes if present
      filename = filenameMatch[1].replace(/['"]/g, '');
    }
  }

  // Create blob URL and trigger download
  const blob = new Blob([response.data], { type: mimeType });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();

  // Cleanup
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

/**
 * Generate a timestamped filename
 * 
 * @param prefix - Prefix for the filename
 * @param extension - File extension (without dot)
 * @returns Timestamped filename
 */
export const generateTimestampedFilename = (prefix: string, extension: string): string => {
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
  return `${prefix}_${timestamp}.${extension}`;
};

